import { FunctionsHttpError } from '@supabase/supabase-js';
import { useTranslation } from 'react-i18next';

import { isPostgrestError } from '@/shared/utils/database-errors';

export type AppError =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'BAD_REQUEST'
  | 'INTERNAL_SERVER_ERROR'
  | 'UNKNOWN_ERROR'
  | 'RATE_LIMIT'
  | 'SUBPROJECT_QUOTA_EXCEEDED'
  | 'SEATS_QUOTA_EXCEEDED'
  | 'STANDARD_KPI_QUOTA_EXCEEDED'
  | 'STANDARD_KPI_NOT_ALLOWED_FOR_CONTRACTOR'
  | 'STANDARD_KPI_NOT_ALLOWED_FOR_SUBPROJECT'
  | 'DERIVATIVE_KPI_QUOTA_EXCEEDED'
  | 'STORAGE_LIMIT_EXCEEDED'
  | 'AI_FEATURE_NOT_AVAILABLE'
  | 'PARTNER_ALREADY_EXISTS'
  | 'ORGANIZATION_SUBSCRIPTION_REQUIRED'
  | 'NO_SUFFICIENT_ORG_ACCESS';

export async function handleError(error: unknown): Promise<AppError> {
  let errorCode: string | undefined;

  if (typeof error === 'string') {
    errorCode = error.toLowerCase();
  } else if (error instanceof FunctionsHttpError) {
    const errorResponse = await error.context.json();
    errorCode = errorResponse.error.toLowerCase();
  } else if (isPostgrestError(error)) {
    errorCode = error.message.toLowerCase();
  } else if (error instanceof Error) {
    errorCode = error.message.toLowerCase();
  } else {
    errorCode = undefined;
  }

  switch (errorCode) {
    case 'unauthorized':
      return 'UNAUTHORIZED';
    case 'forbidden':
      return 'FORBIDDEN';
    case 'not_found':
      return 'NOT_FOUND';
    case 'bad_request':
      return 'BAD_REQUEST';
    case 'internal_server_error':
      return 'INTERNAL_SERVER_ERROR';
    case 'rate_limit':
      return 'RATE_LIMIT';
    case 'subproject_quota_exceeded':
      return 'SUBPROJECT_QUOTA_EXCEEDED';
    case 'seats_quota_exceeded':
      return 'SEATS_QUOTA_EXCEEDED';
    case 'standard_kpi_quota_exceeded':
      return 'STANDARD_KPI_QUOTA_EXCEEDED';
    case 'standard_kpi_not_allowed_for_contractor':
      return 'STANDARD_KPI_NOT_ALLOWED_FOR_CONTRACTOR';
    case 'standard_kpi_not_allowed_for_subproject':
      return 'STANDARD_KPI_NOT_ALLOWED_FOR_SUBPROJECT';
    case 'derivative_kpi_quota_exceeded':
      return 'DERIVATIVE_KPI_QUOTA_EXCEEDED';
    case 'storage_limit_exceeded':
      return 'STORAGE_LIMIT_EXCEEDED';
    case 'ai_feature_not_available':
      return 'AI_FEATURE_NOT_AVAILABLE';
    case 'partner_already_exists':
      return 'PARTNER_ALREADY_EXISTS';
    case 'organization_subscription_required':
      return 'ORGANIZATION_SUBSCRIPTION_REQUIRED';
    case 'no_sufficient_org_access':
      return 'NO_SUFFICIENT_ORG_ACCESS';
  }

  return 'UNKNOWN_ERROR';
}

export const useErrorTranslation = () => {
  const { t } = useTranslation('shared');
  const { t: projectT } = useTranslation('project', { keyPrefix: 'Errors' });
  const { t: subprojectT } = useTranslation('subproject', {
    keyPrefix: 'Shared.errors',
  });
  const { t: subscriptionsT } = useTranslation('subscriptions', {
    keyPrefix: 'errors',
  });

  return (error: unknown): string => {
    switch (error) {
      case 'UNAUTHORIZED':
        return t('errors.unauthorized');
      case 'FORBIDDEN':
        return t('errors.forbidden');
      case 'NOT_FOUND':
        return t('errors.not_found');
      case 'BAD_REQUEST':
        return t('errors.bad_request');
      case 'INTERNAL_SERVER_ERROR':
        return t('errors.internal_server_error');
      case 'UNKNOWN_ERROR':
        return t('errors.unknown_error');
      case 'RATE_LIMIT':
        return t('errors.rate_limit');
      case 'SUBPROJECT_QUOTA_EXCEEDED':
        return projectT('subproject_quota_exceeded');
      case 'SEATS_QUOTA_EXCEEDED':
        return projectT('seats_quota_exceeded');
      case 'DERIVATIVE_KPI_QUOTA_EXCEEDED':
        return projectT('derivative_kpi_quota_exceeded');
      case 'PARTNER_ALREADY_EXISTS':
        return subprojectT('partner_already_exists');
      case 'NO_SUFFICIENT_ORG_ACCESS':
        return t('errors.no_sufficient_org_access');
      case 'ORGANIZATION_SUBSCRIPTION_REQUIRED':
        return subscriptionsT('org_plan_required');
      default:
        return t('errors.unknown_error');
    }
  };
};
